import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Sparkles, Shirt, Trophy, Flame, Compass, ArrowUpRight } from 'lucide-react';

const catalogs = [
  {
    title: "Special Design Brandet 2026",
    url: "https://viewer.joomag.com/special-design-brandet-2026/0104770001738794067?short",
    image: "https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/5bb4dbb7-73c7-40b3-ba16-a4713aef8aa0.jpg",
    badge: "Katalog 2026",
    description: "Udforsk vores nyeste flagskibskollektion af specialdesignet tøj og unikke promotion-artikler til 2026.",
    icon: Sparkles
  },
  {
    title: "Clear Collar 2024",
    url: "https://viewer.joomag.com/clear-collar-2023/0728962001692734090?short",
    image: "https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/e3c69d40-162b-4705-84c9-d4d6da7744ef.jpg",
    badge: "Kollektion 2024",
    description: "Eksklusivt profiltøj med fokus på detaljen, perfekt pasform og kompromisløs kvalitet.",
    icon: Shirt
  },
  {
    title: "Custom Run 2026",
    url: "https://viewer.joomag.com/cstm-run-2026/0851576001770278574?short",
    image: "https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/2a63b5af-1023-4384-aebe-aed386fae512.jpg",
    badge: "Sport 2026",
    description: "Specialdesignet løbetøj og funktionel beklædning optimeret til løbeklubber, events og virksomheder.",
    icon: Flame
  },
  {
    title: "Custom Bike 2026",
    url: "https://viewer.joomag.com/cstm-bike-2026/0920825001770278558?short",
    image: "https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/df425397-937b-4e49-a4a2-52cd082f8d34.jpg",
    badge: "Cykling 2026",
    description: "Professionelt cykeltøj i eget design med optimal komfort, åndbarhed og aerodynamik.",
    icon: Compass
  },
  {
    title: "Custom Team 2026",
    url: "https://viewer.joomag.com/cstm-team-2026/0144892001770278648?short",
    image: "https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/234274d7-9bb3-44a9-8862-3f137d5971fa.jpg",
    badge: "Teamwear 2026",
    description: "Unikt teamwear og foreningstøj skræddersyet til jeres holds identitet og fællesskab.",
    icon: Trophy
  },
  {
    title: "CSTM Teamwear",
    url: "https://viewer.joomag.com/cstm-2025/0237568001779888074?short",
    image: "https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/17f5966c-e19d-43c2-b8fa-cf8872a90074.jpg",
    badge: "Sport & Fritid",
    description: "Højtydende sportsbeklædning og holdtøj med fuld designfrihed og slidstærke materialer.",
    icon: Trophy
  },
  {
    title: "Branded SS25",
    url: "https://viewer.joomag.com/branded-ss25/0038504001779885907?short",
    image: "https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/12595292-b267-4f70-80c3-f839ad5732f0.jpg",
    badge: "Sæson SS25",
    description: "Forår- og sommerkollektionen 2025 med moderne profiltøj, accessories og gaver.",
    icon: Shirt
  }
];

export default function Specialdesign() {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative isolate overflow-hidden bg-white py-20 sm:py-24 lg:py-28 min-h-[400px] flex items-center border-b border-gray-100">
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-slate-50 via-slate-100/50 to-white" />
        <div className="mx-auto max-w-7xl px-6 lg:px-8 w-full">
          <div className="max-w-3xl text-left">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-x-2 rounded-full bg-orange-50 px-3 py-1 text-sm font-semibold leading-6 text-orange-600 ring-1 ring-inset ring-orange-600/10 mb-4"
            >
              <Sparkles className="h-4 w-4" />
              <span>Skræddersyede Løsninger</span>
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl font-bold tracking-tight text-[#1e335e] sm:text-7xl uppercase"
            >
              Specialdesign
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 text-xl leading-8 text-gray-900 font-medium"
            >
              Skab jeres helt eget unikke udtryk. Vi hjælper jer med at designe og producere beklædning og promotion-artikler helt fra bunden, tilpasset jeres specifikke brand, farver og ønsker.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Intro Section */}
      <div className="mx-auto max-w-7xl px-6 py-12 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight text-[#1e335e] sm:text-4xl uppercase">Vores Interaktive Kataloger</h2>
          <p className="mt-4 text-lg text-gray-600">
            Bladr igennem vores specialdesignede kataloger nedenfor for at finde inspiration til jeres næste projekt. Klik på knapperne for at åbne katalogerne i et nyt vindue.
          </p>
        </div>

        {/* Catalog Grid */}
        <div className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {catalogs.map((catalog, index) => {
            const IconComponent = catalog.icon;
            // Cloudflare image resizing for optimized performance
            const optimizedImage = catalog.image.replace(
              "https://assets.auxodev.dk/assets/",
              "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/"
            );

            return (
              <motion.div
                key={catalog.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex flex-col overflow-hidden border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                {/* Image Container */}
                <div className="relative aspect-[4/3] overflow-hidden bg-gray-100 group">
                  <img
                    src={optimizedImage}
                    alt={catalog.title}
                    className="h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end p-4">
                    <span className="text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                      Åbn katalog <ArrowUpRight className="h-3 w-3" />
                    </span>
                  </div>
                  {/* Badge */}
                  <div className="absolute top-4 left-4 bg-[#1e335e] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1">
                    {catalog.badge}
                  </div>
                </div>

                {/* Content */}
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-x-2 text-orange-500 mb-3">
                      <IconComponent className="h-5 w-5" />
                      <span className="text-xs font-bold uppercase tracking-wider">Specialdesign</span>
                    </div>
                    <h3 className="text-xl font-bold text-[#1e335e] uppercase tracking-wide mb-2">
                      {catalog.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-6">
                      {catalog.description}
                    </p>
                  </div>

                  {/* Button */}
                  <a
                    href={catalog.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-x-2 text-xs font-bold uppercase tracking-wider text-white bg-orange-500 px-5 py-3.5 hover:bg-orange-600 transition-all w-full"
                  >
                    <span>Se katalog</span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-slate-50 border-t border-gray-100 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-[#1e335e] sm:text-4xl uppercase">Har I et specifikt ønske?</h2>
          <p className="mx-auto mt-4 max-w-xl text-lg leading-8 text-gray-600">
            Lad os tage en uforpligtende snak om, hvordan vi kan hjælpe jer med at realisere jeres designidéer.
          </p>
          <div className="mt-8 flex items-center justify-center gap-x-6">
            <a
              href="/kontakt"
              className="text-xs font-bold uppercase tracking-wider text-white bg-[#1e335e] px-6 py-3.5 hover:bg-[#13223f] transition-all"
            >
              Kontakt os i dag
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
